// "Continue watching" rail — surfaces the last 8 items the user
// listened to or watched. Sits high on the Home page so people can
// resume what they were already enjoying with a single tap.
//
// Each tile carries the stored item_id + item_type so we can route
// directly to the right player (`/sermons`, `/movies`, etc.).
// Anonymous users see nothing — the rail just doesn't render.
//
// Long-press a tile (or hover on desktop) to reveal an "X" remove
// button — the user can scrub finished items out of their history.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { listHistory, deleteHistoryItem, logWatch } from "../lib/history";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const ROUTE_FOR_TYPE = {
  movie: "/movies",
  film: "/movies",
  video: "/videos",
  sermon: "/sermons",
  lesson: "/lessons",
  music: "/music",
  gospel: "/music",
  song: "/music",
  story: "/stories",
  cartoon: "/cartoons",
  bible: "/bible",
};

function tileRoute(item) {
  const t = (item.item_type || "").toLowerCase();
  return ROUTE_FOR_TYPE[t] || `/search?q=${encodeURIComponent(item.title || "")}`;
}

function ContinueTile({ item, onRemove, progressPct = 0 }) {
  const [showRemove, setShowRemove] = useState(false);
  const longPressRef = useRef(null);

  const clearLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  const onPointerDown = () => {
    clearLongPress();
    longPressRef.current = setTimeout(() => {
      setShowRemove(true);
      // Suppress the synthetic click that would otherwise navigate to
      // the player when the user releases their long-press.
      const suppress = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();
      };
      document.addEventListener("click", suppress, { capture: true, once: true });
      setTimeout(() => { try { document.removeEventListener("click", suppress, true); } catch (_) {} }, 700);
    }, 550);
  };

  useEffect(() => () => clearLongPress(), []);

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove();
  };

  return (
    <div className="relative snap-start shrink-0 w-44 md:w-52 group">
      <Link
        to={tileRoute(item)}
        data-testid={`continue-watching-tile-${item.item_id}`}
        onPointerDown={onPointerDown}
        onPointerUp={clearLongPress}
        onPointerCancel={clearLongPress}
        onPointerLeave={clearLongPress}
        className="block"
      >
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-forest/10 border border-forest/10 group-hover:border-terracotta/40 transition-all">
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-forest/40">
              <Clock size={28} weight="duotone" />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-ink/85 to-transparent" />
          <span className="absolute bottom-2 left-2 right-2 text-[10px] tracking-[0.18em] uppercase text-cream font-bold">
            {progressPct >= 2 ? `Resume · ${progressPct}%` : "Resume"}
          </span>
          {/* Slim terracotta progress bar at the very bottom edge of
              the thumbnail — matches Netflix/YouTube convention so
              users instantly recognize what they're looking at. */}
          {progressPct >= 2 && (
            <div
              className="absolute left-0 right-0 bottom-0 h-1 bg-cream/20"
              data-testid={`continue-watching-progress-${item.item_id}`}
            >
              <div
                className="h-full bg-terracotta"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
        </div>
        <p className="mt-2 text-sm font-medium text-ink line-clamp-2 leading-tight">{item.title || "Untitled"}</p>
        {item.subtitle && (
          <p className="text-xs text-muted_ink line-clamp-1 mt-0.5">{item.subtitle}</p>
        )}
      </Link>
      {(showRemove || true) && (
        <button
          type="button"
          onClick={handleRemove}
          onPointerDown={(e) => e.stopPropagation()}
          data-testid={`continue-watching-remove-${item.item_id}`}
          aria-label="Remove from history"
          title="Remove from history"
          className={`absolute top-2 right-2 w-7 h-7 rounded-full bg-ink/85 hover:bg-red-600 text-cream grid place-items-center backdrop-blur shadow-lg transition-opacity ${showRemove ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        >
          <X size={12} weight="bold" />
        </button>
      )}
    </div>
  );
}

export default function ContinueWatchingRail() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [progressMap, setProgressMap] = useState({});

  const load = () => {
    if (!user) { setItems([]); setProgressMap({}); return; }
    listHistory("watch")
      .then((rows) => {
        const seen = new Map();
        for (const r of rows) {
          if (!seen.has(r.item_id)) seen.set(r.item_id, r);
        }
        setItems(Array.from(seen.values()).slice(0, 8));
      })
      .catch(() => setItems([]));
    // Merge in resumable movie-progress so each tile can render a
    // terracotta progress bar showing exactly how far through the
    // film the user is. Defers to idle so this never blocks the
    // initial render of the rail.
    const fetchProgress = () => {
      api.get("/movies/progress/me")
        .then((r) => {
          const map = {};
          for (const row of (r.data?.items || [])) {
            const secs = Number(row.seconds || 0);
            const dur = Number(row.duration_sec || 0);
            if (dur > 0) {
              map[row.movie_id] = Math.min(99, Math.max(2, Math.round((secs / dur) * 100)));
            }
          }
          setProgressMap(map);
        })
        .catch(() => {});
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(fetchProgress, { timeout: 1500 });
    } else {
      setTimeout(fetchProgress, 300);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  const onRemove = async (item) => {
    // Optimistic remove for instant feedback.
    setItems((prev) => prev.filter((x) => x.item_id !== item.item_id));
    // Defer the server delete by 5 seconds so we have a window during
    // which the user can hit Undo. If they do, we just bail before
    // the network call ever fires.
    let cancelled = false;
    const tid = setTimeout(async () => {
      if (cancelled) return;
      try {
        await deleteHistoryItem({ kind: "watch", itemId: item.item_id });
      } catch (_) {
        load();
      }
    }, 5000);
    toast("Removed from your history", {
      description: item.title || "Untitled",
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          cancelled = true;
          clearTimeout(tid);
          // Restore the tile at the front of the list immediately.
          setItems((prev) => [item, ...prev.filter((x) => x.item_id !== item.item_id)].slice(0, 8));
          // Re-log so server-side history is back too.
          try {
            logWatch({
              id: item.item_id,
              type: item.item_type,
              title: item.title,
              subtitle: item.subtitle,
              thumbnail: item.thumbnail,
            });
          } catch (_) {}
        },
      },
    });
  };

  if (!user || items.length === 0) return null;

  return (
    <section className="mt-10 md:mt-14" data-testid="home-continue-watching">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-terracotta flex items-center gap-1.5">
            <Clock size={11} weight="fill" /> Pick up where you left off
          </p>
          <h2 className="font-heading text-2xl md:text-3xl text-forest mt-1.5 leading-tight">
            Continue watching
          </h2>
        </div>
        <Link to="/history" data-testid="continue-watching-see-all" className="text-xs text-terracotta hover:underline inline-flex items-center gap-1">
          See all <ArrowRight size={12} weight="bold" />
        </Link>
      </div>

      <div
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 md:mx-0 md:px-0"
        style={{ scrollbarWidth: "thin" }}
        data-testid="continue-watching-rail"
      >
        {items.map((it) => (
          <ContinueTile key={it.item_id} item={it} progressPct={progressMap[it.item_id] || 0} onRemove={() => onRemove(it)} />
        ))}
      </div>
    </section>
  );
}
